import React, { useState } from 'react';
import {
    Box,
    Typography,
    Alert,
    Stack
} from '@mui/material';
import { sectionTitleStyles } from '@Pages/RnDMember/ExitRequestResponseUnified/ExitRequestResponseStyles';

// Local imports
import { CommentPopup } from './CommentPopup';
import { DepartmentSupportSection } from './DepartmentSupportSection';
import {
    LABELS,
    DEPARTMENTS
} from './SupportDetailsTab.constants';
import {
    getCommentForUser,
    getPopupTitle
} from './SupportDetailsTab.utils';

export const SupportDetailsTab = ({
                                      needsSupport,
                                      marketingSupport,
                                      supplyChainSupport,
                                      selectedMarketingMember,
                                      selectedSupplyChainMember,
                                      marketingSupportProvided,
                                      supplyChainSupportProvided,
                                      comments,
                                      theme
                                  }) => {
    // ================ Local State ================
    const [popupState, setPopupState] = useState({
        open: false,
        title: '',
        comment: ''
    });

    // ================ Handlers ================
    const handleOpenPopup = (title, comment) => {
        setPopupState({
            open: true,
            title,
            comment
        });
    };

    const handleClosePopup = () => {
        setPopupState({
            open: false,
            title: '',
            comment: ''
        });
    };

    // ================ Render Conditions ================
    if (!needsSupport) {
        return (
            <Box sx={styles.container}>
                <Typography variant="h6" sx={sectionTitleStyles(theme)}>
                    {LABELS.SUPPORT_REQUEST}
                </Typography>
                <Alert severity="info" sx={styles.noSupportAlert}>
                    {LABELS.NO_SUPPORT_MESSAGE}
                </Alert>
            </Box>
        );
    }

    // ================ Main Render ================
    return (
        <Box sx={styles.containerWithSupport}>
            <Typography variant="h6" sx={sectionTitleStyles(theme)}>
                {LABELS.SUPPORT_REQUEST}
            </Typography>

            <Stack spacing={2}>
                {/* Marketing Support Section */}
                {marketingSupport && (
                    <DepartmentSupportSection
                        departmentType={DEPARTMENTS.MARKETING}
                        departmentLabel={LABELS.MARKETING_DEPARTMENT}
                        supportProvided={marketingSupportProvided}
                        selectedMember={selectedMarketingMember}
                        comment={getCommentForUser(selectedMarketingMember, comments)}
                        onExpandComment={() => {
                            const comment = getCommentForUser(selectedMarketingMember, comments);
                            if (comment) {
                                handleOpenPopup(
                                    getPopupTitle(selectedMarketingMember, LABELS.MARKETING_DEPARTMENT),
                                    comment.comment
                                );
                            }
                        }}
                        theme={theme}
                    />
                )}

                {/* Supply Chain Support Section */}
                {supplyChainSupport && (
                    <DepartmentSupportSection
                        departmentType={DEPARTMENTS.SUPPLY_CHAIN}
                        departmentLabel={LABELS.SUPPLY_CHAIN_DEPARTMENT}
                        supportProvided={supplyChainSupportProvided}
                        selectedMember={selectedSupplyChainMember}
                        comment={getCommentForUser(selectedSupplyChainMember, comments)}
                        onExpandComment={() => {
                            const comment = getCommentForUser(selectedSupplyChainMember, comments);
                            if (comment) {
                                handleOpenPopup(
                                    getPopupTitle(selectedSupplyChainMember, LABELS.SUPPLY_CHAIN_DEPARTMENT),
                                    comment.comment
                                );
                            }
                        }}
                        theme={theme}
                    />
                )}
            </Stack>

            {/* Comment Popup */}
            <CommentPopup
                open={popupState.open}
                onClose={handleClosePopup}
                title={popupState.title}
                comment={popupState.comment}
            />
        </Box>
    );
};

// ================ Component Styles ================
const styles = {
    container: {
        maxHeight: "100vh",
        overflowY: 'auto'
    },
    containerWithSupport: {
        maxHeight: "68vh",
        overflowY: 'auto'
    },
    noSupportAlert: {
        mb: 2,
        fontSize: '0.85rem'
    }
};